import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { OutcomeRangesSection } from "@/components/admin/OutcomeRangesSection"
import { SaveOptionsPanel } from "@/components/admin/SaveOptionsPanel"
import { ChevronDown, Dices, Lock, Unlock, Plus, X, AlertTriangle } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  getDefaultOutcomeTypes,
  getDefaultOutcomeRanges,
  type IOutcomeType,
  type IOutcomeRanges,
} from "@/lib/slotMachineTypes"

interface SimpleTogglesProps {
  outcomeTypes: IOutcomeType[]
  outcomeRanges: IOutcomeRanges
  onOutcomeTypesChange: (types: IOutcomeType[]) => void
  onOutcomeRangesChange: (ranges: IOutcomeRanges) => void
  disabled?: boolean
}

const CASINO_COLORS = {
  bg: "#121220",
  bgLight: "#1a1a2e",
  orange: "#F37020",
  gold: "#FFD700",
  green: "#32CD32",
  red: "#FF4444",
  border: "rgba(255,255,255,0.1)",
  text: "rgba(255,255,255,0.9)",
  textMuted: "rgba(255,255,255,0.6)",
}

// Default types that cannot be deleted
const DEFAULT_TYPE_NAMES = ["points", "bonus", "merchandise", "freeSpins", "quest", "miss"]

// Auto-balance algorithm: distribute delta proportionally among other enabled sliders
const autoBalanceTypes = (
  types: IOutcomeType[],
  changedIndex: number,
  newValue: number
): IOutcomeType[] => {
  const result = [...types]
  const oldValue = result[changedIndex].probability
  const delta = newValue - oldValue
  
  const otherEnabled = result.filter((t, i) => i !== changedIndex && t.enabled)
  const otherTotal = otherEnabled.reduce((sum, t) => sum + t.probability, 0)
  
  if (otherTotal === 0 && delta > 0) {
    return result
  }
  
  const maxValue = Math.min(100, oldValue + otherTotal)
  const clampedValue = Math.max(0, Math.min(maxValue, newValue))
  const actualDelta = clampedValue - oldValue
  
  result[changedIndex] = { ...result[changedIndex], probability: clampedValue }
  
  if (actualDelta !== 0 && otherTotal > 0) {
    for (let i = 0; i < result.length; i++) {
      if (i !== changedIndex && result[i].enabled) {
        const proportion = result[i].probability / otherTotal
        const adjustment = -actualDelta * proportion
        result[i] = {
          ...result[i],
          probability: Math.max(0, Math.round((result[i].probability + adjustment) * 100) / 100),
        }
      }
    }
  }
  
  const total = result.filter(t => t.enabled).reduce((sum, t) => sum + t.probability, 0)
  if (Math.abs(total - 100) > 0.01) {
    const diff = 100 - total
    const largestIdx = result
      .map((t, i) => ({ t, i }))
      .filter(({ t, i }) => t.enabled && i !== changedIndex)
      .sort((a, b) => b.t.probability - a.t.probability)[0]?.i
    if (largestIdx !== undefined) {
      result[largestIdx] = {
        ...result[largestIdx],
        probability: Math.round((result[largestIdx].probability + diff) * 100) / 100,
      }
    }
  }
  
  return result
}

// Redistribute probability when adding new type
const addNewType = (types: IOutcomeType[], name: string, initialProbability: number): IOutcomeType[] => {
  const enabledTypes = types.filter(t => t.enabled)
  const totalToReduce = initialProbability
  const otherTotal = enabledTypes.reduce((sum, t) => sum + t.probability, 0)
  
  const newTypes = types.map(t => {
    if (!t.enabled) return t
    const proportion = t.probability / otherTotal
    const reduction = totalToReduce * proportion
    return {
      ...t,
      probability: Math.max(0, Math.round((t.probability - reduction) * 100) / 100),
    }
  })
  
  // Add the new type
  newTypes.push({
    name,
    probability: initialProbability,
    enabled: true,
  })
  
  // Fix rounding
  const total = newTypes.filter(t => t.enabled).reduce((s, t) => s + t.probability, 0)
  if (Math.abs(total - 100) > 0.01) {
    const firstEnabled = newTypes.findIndex(t => t.enabled)
    if (firstEnabled >= 0) {
      newTypes[firstEnabled].probability = Math.round((newTypes[firstEnabled].probability + (100 - total)) * 100) / 100
    }
  }
  
  return newTypes
}

// Redistribute probability when deleting type
const deleteType = (types: IOutcomeType[], indexToDelete: number): IOutcomeType[] => {
  const probabilityToRedistribute = types[indexToDelete].probability
  const wasEnabled = types[indexToDelete].enabled
  const remaining = types.filter((_, i) => i !== indexToDelete)
  
  if (!wasEnabled || probabilityToRedistribute === 0) {
    return remaining
  }
  
  const enabledTypes = remaining.filter(t => t.enabled)
  const enabledTotal = enabledTypes.reduce((sum, t) => sum + t.probability, 0)
  
  return remaining.map(t => {
    if (!t.enabled || enabledTotal === 0) return t
    const proportion = t.probability / enabledTotal
    const addition = probabilityToRedistribute * proportion
    return {
      ...t,
      probability: Math.round((t.probability + addition) * 100) / 100,
    }
  })
}

function CollapsibleSection({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  rightElement,
}: {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  defaultOpen?: boolean
  rightElement?: React.ReactNode
}) {
  const [open, setOpen] = React.useState(defaultOpen)

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200 hover:border-[#F37020]/40"
          style={{
            backgroundColor: CASINO_COLORS.bgLight,
            borderColor: open ? CASINO_COLORS.orange : CASINO_COLORS.border,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${CASINO_COLORS.orange}20` }}
            >
              <Icon className="w-4 h-4" style={{ color: CASINO_COLORS.orange }} />
            </div>
            <span
              className="text-sm font-semibold font-outfit"
              style={{ color: CASINO_COLORS.text }}
            >
              {title}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {rightElement}
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown
                className="w-4 h-4"
                style={{ color: CASINO_COLORS.textMuted }}
              />
            </motion.div>
          </div>
        </button>
      </CollapsibleTrigger>
      <AnimatePresence initial={false}>
        {open && (
          <CollapsibleContent forceMount asChild>
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="overflow-hidden"
            >
              <div className="pt-3 pb-1">{children}</div>
            </motion.div>
          </CollapsibleContent>
        )}
      </AnimatePresence>
    </Collapsible>
  )
}

// Custom slider with casino styling
function ProbabilitySlider({
  value,
  onChange,
  disabled,
  enabled,
  onToggleEnabled,
  name,
  canDelete,
  onDelete,
}: {
  value: number
  onChange: (value: number) => void
  disabled?: boolean
  enabled: boolean
  onToggleEnabled: () => void
  name: string
  canDelete?: boolean
  onDelete?: () => void
}) {
  const [isDragging, setIsDragging] = React.useState(false)

  return (
    <motion.div
      layout
      className="rounded-lg border p-3 transition-all duration-200 relative"
      style={{
        backgroundColor: CASINO_COLORS.bg,
        borderColor: enabled ? `${CASINO_COLORS.orange}40` : CASINO_COLORS.border,
        opacity: enabled ? 1 : 0.5,
      }}
    >
      {/* Delete button for custom types */}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center bg-red-500/90 hover:bg-red-500 transition-colors z-10"
          title="Delete outcome type"
        >
          <X className="w-3 h-3 text-white" />
        </button>
      )}
      
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleEnabled}
            disabled={disabled}
            className="p-1 rounded transition-colors hover:bg-white/10"
            title={enabled ? "Disable outcome" : "Enable outcome"}
          >
            {enabled ? (
              <Unlock className="w-3.5 h-3.5" style={{ color: CASINO_COLORS.green }} />
            ) : (
              <Lock className="w-3.5 h-3.5" style={{ color: CASINO_COLORS.textMuted }} />
            )}
          </button>
          <span
            className="text-sm font-outfit capitalize"
            style={{ color: enabled ? CASINO_COLORS.text : CASINO_COLORS.textMuted }}
          >
            {name.replace(/([A-Z])/g, " $1").trim()}
          </span>
        </div>
        <motion.span
          key={value}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          className="text-sm font-mono font-bold px-2 py-0.5 rounded min-w-[52px] text-center"
          style={{
            backgroundColor: isDragging ? `${CASINO_COLORS.orange}30` : `${CASINO_COLORS.orange}20`,
            color: CASINO_COLORS.orange,
          }}
        >
          {value.toFixed(1)}%
        </motion.span>
      </div>
      
      <div className="relative h-6 flex items-center">
        <div
          className="absolute inset-0 rounded-full h-2 top-1/2 -translate-y-1/2"
          style={{ backgroundColor: `${CASINO_COLORS.border}` }}
        />
        <motion.div
          className="absolute left-0 rounded-full h-2 top-1/2 -translate-y-1/2"
          style={{
            width: `${value}%`,
            backgroundColor: enabled ? CASINO_COLORS.orange : CASINO_COLORS.textMuted,
          }}
          animate={{ width: `${value}%` }}
          transition={{ duration: 0.1 }}
        />
        <input
          type="range"
          min="0"
          max="100"
          step="0.5"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          disabled={disabled || !enabled}
          className="relative w-full h-6 appearance-none bg-transparent cursor-pointer disabled:cursor-not-allowed z-10"
          style={{
            WebkitAppearance: "none",
          }}
        />
      </div>
      
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: ${CASINO_COLORS.orange};
          border: 2px solid ${CASINO_COLORS.bg};
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transition: transform 0.15s ease;
        }
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.15);
        }
        input[type="range"]::-webkit-slider-thumb:active {
          transform: scale(1.25);
        }
        input[type="range"]:disabled::-webkit-slider-thumb {
          background: ${CASINO_COLORS.textMuted};
          cursor: not-allowed;
        }
        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: ${CASINO_COLORS.orange};
          border: 2px solid ${CASINO_COLORS.bg};
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
      `}</style>
    </motion.div>
  )
}

// Add Outcome Type Modal
function AddOutcomeModal({
  open,
  onOpenChange,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (name: string, probability: number) => void
}) {
  const [name, setName] = React.useState("")
  const [probability, setProbability] = React.useState(10)
  const [error, setError] = React.useState("")

  const handleAdd = () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError("Name is required")
      return
    }
    if (trimmedName.length < 2) {
      setError("Name must be at least 2 characters")
      return
    }
    if (probability <= 0) {
      setError("Probability must be greater than 0")
      return
    }
    onAdd(trimmedName, probability)
    setName("")
    setProbability(10)
    setError("")
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md w-[95vw] sm:w-full"
        style={{
          backgroundColor: CASINO_COLORS.bg,
          borderColor: CASINO_COLORS.border,
          color: CASINO_COLORS.text,
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2" style={{ color: CASINO_COLORS.gold }}>
            <Plus className="w-5 h-5" />
            Add Outcome Type
          </DialogTitle>
          <DialogDescription style={{ color: CASINO_COLORS.textMuted }}>
            Create a custom outcome type for the slot machine. Probabilities will auto-balance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name Input */}
          <div className="space-y-2">
            <label className="text-sm font-outfit" style={{ color: CASINO_COLORS.text }}>
              Outcome Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setError("")
              }}
              placeholder="e.g., Jackpot, Lucky7, Double"
              className="w-full px-3 py-2 rounded-lg border text-sm font-outfit"
              style={{
                backgroundColor: CASINO_COLORS.bgLight,
                borderColor: error ? CASINO_COLORS.red : CASINO_COLORS.border,
                color: CASINO_COLORS.text,
              }}
            />
          </div>

          {/* Probability Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-outfit" style={{ color: CASINO_COLORS.text }}>
                Initial Probability
              </label>
              <span
                className="text-sm font-mono font-bold px-2 py-0.5 rounded"
                style={{
                  backgroundColor: `${CASINO_COLORS.orange}20`,
                  color: CASINO_COLORS.orange,
                }}
              >
                {probability.toFixed(1)}%
              </span>
            </div>
            <div className="relative h-8 flex items-center">
              <div
                className="absolute inset-0 rounded-full h-2 top-1/2 -translate-y-1/2"
                style={{ backgroundColor: CASINO_COLORS.border }}
              />
              <div
                className="absolute left-0 rounded-full h-2 top-1/2 -translate-y-1/2"
                style={{
                  width: `${probability}%`,
                  backgroundColor: CASINO_COLORS.orange,
                }}
              />
              <input
                type="range"
                min="1"
                max="50"
                step="0.5"
                value={probability}
                onChange={(e) => setProbability(parseFloat(e.target.value))}
                className="relative w-full h-8 appearance-none bg-transparent cursor-pointer z-10"
              />
            </div>
            <p className="text-xs" style={{ color: CASINO_COLORS.textMuted }}>
              This probability will be taken proportionally from other outcomes.
            </p>
          </div>

          {error && (
            <p className="text-sm" style={{ color: CASINO_COLORS.red }}>
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm font-outfit transition-colors"
            style={{
              backgroundColor: CASINO_COLORS.bgLight,
              color: CASINO_COLORS.textMuted,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            className="px-4 py-2 rounded-lg text-sm font-outfit font-semibold transition-colors"
            style={{
              backgroundColor: CASINO_COLORS.orange,
              color: "white",
            }}
          >
            Add Outcome
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Delete Confirmation Modal
function DeleteConfirmModal({
  open,
  onOpenChange,
  typeName,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  typeName: string
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm w-[95vw] sm:w-full"
        style={{
          backgroundColor: CASINO_COLORS.bg,
          borderColor: CASINO_COLORS.border,
          color: CASINO_COLORS.text,
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2" style={{ color: CASINO_COLORS.red }}>
            <AlertTriangle className="w-5 h-5" />
            Delete Outcome Type
          </DialogTitle>
          <DialogDescription style={{ color: CASINO_COLORS.textMuted }}>
            Are you sure you want to delete &quot;<span style={{ color: CASINO_COLORS.text }}>{typeName}</span>&quot;? 
            Its probability will be redistributed to other outcomes.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-0 pt-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm font-outfit transition-colors"
            style={{
              backgroundColor: CASINO_COLORS.bgLight,
              color: CASINO_COLORS.textMuted,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm()
              onOpenChange(false)
            }}
            className="px-4 py-2 rounded-lg text-sm font-outfit font-semibold transition-colors"
            style={{
              backgroundColor: CASINO_COLORS.red,
              color: "white",
            }}
          >
            Delete
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SimpleToggles({
  outcomeTypes,
  outcomeRanges,
  onOutcomeTypesChange,
  onOutcomeRangesChange,
  disabled = false,
}: SimpleTogglesProps) {
  const [types, setTypes] = React.useState<IOutcomeType[]>(() =>
    outcomeTypes.length > 0 ? outcomeTypes : getDefaultOutcomeTypes()
  )
  const ranges = Object.keys(outcomeRanges).length > 0 ? outcomeRanges : getDefaultOutcomeRanges()

  // Modal state
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [deleteConfirm, setDeleteConfirm] = React.useState<{ index: number; name: string } | null>(null)

  React.useEffect(() => {
    if (outcomeTypes.length > 0) {
      setTypes(outcomeTypes)
    }
  }, [outcomeTypes])

  const total = types.filter(t => t.enabled).reduce((sum, t) => sum + t.probability, 0)
  const isBalanced = Math.abs(total - 100) < 0.1

  const handleSliderChange = (index: number, newValue: number) => {
    const balanced = autoBalanceTypes(types, index, newValue)
    setTypes(balanced)
    onOutcomeTypesChange(balanced)
  }

  const handleToggleEnabled = (index: number) => {
    const newTypes = [...types]
    newTypes[index] = { ...newTypes[index], enabled: !newTypes[index].enabled }
    
    const enabledTypes = newTypes.filter(t => t.enabled)
    if (enabledTypes.length > 0) {
      const perType = 100 / enabledTypes.length
      for (let i = 0; i < newTypes.length; i++) {
        newTypes[i] = {
          ...newTypes[i],
          probability: newTypes[i].enabled ? Math.round(perType * 10) / 10 : 0,
        }
      }
      const newTotal = newTypes.filter(t => t.enabled).reduce((s, t) => s + t.probability, 0)
      if (Math.abs(newTotal - 100) > 0.01) {
        const firstEnabled = newTypes.findIndex(t => t.enabled)
        if (firstEnabled >= 0) {
          newTypes[firstEnabled].probability += 100 - newTotal
        }
      }
    }
    
    setTypes(newTypes)
    onOutcomeTypesChange(newTypes)
  }

  const handleAddType = (name: string, probability: number) => {
    const newTypes = addNewType(types, name, probability)
    setTypes(newTypes)
    onOutcomeTypesChange(newTypes)
  }

  const handleDeleteType = (index: number) => {
    const newTypes = deleteType(types, index)
    setTypes(newTypes)
    onOutcomeTypesChange(newTypes)
    setDeleteConfirm(null)
  }

  const isCustomType = (name: string) => !DEFAULT_TYPE_NAMES.includes(name.toLowerCase())

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="rounded-2xl p-5 border"
        style={{
          backgroundColor: CASINO_COLORS.bg,
          borderColor: CASINO_COLORS.border,
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${CASINO_COLORS.gold}15` }}
          >
            <Dices className="w-5 h-5" style={{ color: CASINO_COLORS.gold }} />
          </div>
          <div>
            <h3
              className="text-base font-bold font-outfit tracking-tight"
              style={{ color: CASINO_COLORS.text }}
            >
              Simple Toggles
            </h3>
            <p
              className="text-xs font-outfit"
              style={{ color: CASINO_COLORS.textMuted }}
            >
              Quick outcome probability controls for slot machine
            </p>
          </div>
        </div>

        {/* Collapsible Sections */}
        <div className="space-y-3">
          <CollapsibleSection
            title="Outcomes"
            icon={({ className }) => (
              <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" y1="9" x2="9.01" y2="9" />
                <line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
            )}
            defaultOpen={true}
            rightElement={
              <div
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-mono"
                style={{
                  backgroundColor: isBalanced ? `${CASINO_COLORS.green}20` : `${CASINO_COLORS.red}20`,
                  color: isBalanced ? CASINO_COLORS.green : CASINO_COLORS.red,
                }}
              >
                <span>Total:</span>
                <span className="font-bold">{total.toFixed(1)}%</span>
              </div>
            }
          >
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: CASINO_COLORS.bgLight }}
            >
              <p
                className="text-xs font-outfit mb-4"
                style={{ color: CASINO_COLORS.textMuted }}
              >
                Drag sliders to adjust probability. Values auto-balance to total 100%.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {types.map((type, index) => (
                  <ProbabilitySlider
                    key={`${type.name}-${index}`}
                    name={type.name}
                    value={type.probability}
                    enabled={type.enabled}
                    disabled={disabled}
                    onChange={(value) => handleSliderChange(index, value)}
                    onToggleEnabled={() => handleToggleEnabled(index)}
                    canDelete={isCustomType(type.name)}
                    onDelete={() => setDeleteConfirm({ index, name: type.name })}
                  />
                ))}
              </div>
              
              {/* Add Outcome Type Button */}
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                disabled={disabled}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  borderColor: `${CASINO_COLORS.orange}60`,
                  color: CASINO_COLORS.orange,
                  backgroundColor: "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${CASINO_COLORS.orange}10`
                  e.currentTarget.style.borderColor = CASINO_COLORS.orange
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent"
                  e.currentTarget.style.borderColor = `${CASINO_COLORS.orange}60`
                }}
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-outfit font-semibold">Add Outcome Type</span>
              </button>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Outcome Ranges"
            icon={({ className }) => (
              <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
            )}
            defaultOpen={false}
          >
            <div
              className="rounded-xl p-4"
              style={{ backgroundColor: CASINO_COLORS.bgLight }}
            >
              <OutcomeRangesSection
                ranges={ranges}
                onChange={onOutcomeRangesChange}
                disabled={disabled}
              />
            </div>
          </CollapsibleSection>
          {/* Save Options Panel */}
          <SaveOptionsPanel
            outcomeTypes={types}
            outcomeRanges={ranges}
            onSaveSuccess={() => console.log("Saved!")}
            onRevert={() => {
              setTypes(getDefaultOutcomeTypes())
              onOutcomeRangesChange(getDefaultOutcomeRanges())
            }}
            disabled={disabled}
          />
        </div>
      </motion.div>

      {/* Modals */}
      <AddOutcomeModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        onAdd={handleAddType}
      />
      <DeleteConfirmModal
        open={deleteConfirm !== null}
        onOpenChange={(open) => !open && setDeleteConfirm(null)}
        typeName={deleteConfirm?.name || ""}
        onConfirm={() => deleteConfirm && handleDeleteType(deleteConfirm.index)}
      />
    </>
  )
}
